/**
 * שורות הביקור והצ׳קליסט כפי שהשרת מחזיר אותן.
 *
 * הכללים (סימונים לפי פרק, ליקוי ← ממצא, חומרה לפני הגשה, נעילת scope)
 * נאכפים בשרת — supabase/migrations/20260924090000_stage4_checklist_visit.sql.
 * המסך מציג אותם מראש בעזרת `summarizeVisit`, אבל השרת הוא הקובע.
 */

import type {
  AnswerScheme,
  AnswerValue,
  ContentVerification,
  FollowupEvidenceSource,
  FollowupState,
  NotCheckedReason,
  Severity,
  VisitKind,
} from '@hakshuri/domain';

export type TemplateVersionRow = {
  id: string;
  title: string;
  version_label: string;
  is_demo: boolean;
  status: 'draft' | 'published' | 'retired';
  based_on_version_id: string | null;
  created_at: string;
  published_at: string | null;
  /** מי פרסם (D37: הפרסום של גרסה שאינה הדגמה הוא האישור המקצועי). */
  published_by: string | null;
};

export type ChapterRow = {
  id: string;
  template_version_id: string;
  code: string;
  title: string;
  answer_scheme: AnswerScheme;
  sort_order: number;
};

export type QuestionRow = {
  id: string;
  template_version_id: string;
  chapter_id: string;
  code: string;
  title: string;
  what_to_check: string;
  source_text: string;
  verification_status: ContentVerification;
  applicability: unknown;
  sort_order: number;
};

export type VisitRow = {
  id: string;
  site_id: string;
  template_version_id: string;
  visit_date: string;
  opened_by: string;
  opened_at: string;
  plan_document_version_id: string | null;
  plan_missing_reason: string | null;
  limitations: string;
  status: 'draft' | 'submitted' | 'reviewed' | 'approved';
  submitted_at: string | null;
  submitted_by: string | null;
  row_version: number;
  /** D22 — חסרים אם השרת עוד לא עודכן למיגרציית 6ד חלק ב׳. */
  visit_kind?: VisitKind | null;
  visit_kind_other?: string | null;
  workers_on_site?: number | null;
  attendees?: string;
  inspector_notes?: string;
};

export type VisitItemRow = { question_id: string; applicable: boolean; reason: string };
export type VisitAreaRow = { area_id: string; area_name: string };
export type VisitStageRow = {
  stage_period_id: string;
  area_id: string;
  stage_name: string;
  /** סוג השלב כפי שננעל בפתיחה (תוכן 0.2); חסר בביקורים שנפתחו לפני המיגרציה. */
  stage_kind?: string;
};

export type AnswerRow = {
  id: string;
  question_id: string;
  state: AnswerValue | null;
  note: string;
  /** סיבה מהירה ל״לא נבדק״ (D14). חסר — אם השרת עוד לא עודכן לשלב 5. */
  not_checked_reason?: NotCheckedReason | null;
  answered_by: string;
  answered_at: string;
  row_version: number;
};

export type FindingRow = {
  id: string;
  question_id: string | null;
  finding_number: number;
  description: string;
  location_text: string;
  area_id: string | null;
  severity: Severity | null;
  created_by: string;
  created_at: string;
  /** מחיקה רכה (D20): ממצא שנמחק אינו מוצג. `withdrawn_reason` — רשות (ישן). */
  withdrawn_at: string | null;
  withdrawn_reason: string | null;
  row_version: number;
};

export type EvidenceRow = {
  id: string;
  /** הביקור (בשורה מהשרת; בתמונה שעוד במכשיר — חסר). */
  visit_id?: string;
  question_id: string | null;
  finding_id: string | null;
  /** D25: תמונת ״אחרי״ — הממצא של רשומת המעקב (נגזר בשרת מאז D28; NULL לסעיף). */
  followup_finding_id?: string | null;
  /** D28: תמונת ״אחרי״ — רשומת המעקב. חסר בשרת ישן / בעותק ישן. */
  followup_id?: string | null;
  /** במכשיר בלבד (תור): מפתח הפריט של תמונת ״אחרי״ שעוד לא עלתה. */
  followup_item_key?: string;
  storage_path: string;
  caption: string;
  observed_at: string | null;
  source: 'camera' | 'upload';
  uploaded_by: string;
  uploaded_at: string;
  withdrawn_at: string | null;
};

export type AlertRow = {
  id: string;
  visit_id: string | null;
  finding_id: string | null;
  description: string;
  location_text: string;
  created_by: string;
  created_at: string;
  /** D25: מתי הסיכון התגלה (רשות). */
  observed_at?: string | null;
};

/** D25/D28: רשומת מעקב לפריט מביקור קודם — ממצא, או סעיף בלי ממצא — כפי שהשרת מחזיר. */
export type FollowupRow = {
  id: string;
  visit_id: string;
  finding_id: string | null;
  /** D28: סעיף בלי ממצא — הביקור המקורי והסעיף. חסר בשרת ישן / בעותק ישן. */
  source_visit_id?: string | null;
  question_id?: string | null;
  state: FollowupState;
  not_checked_reason: NotCheckedReason | null;
  fix_reported_by_text: string;
  fix_reported_on: string | null;
  fix_report_text: string;
  evidence_source: FollowupEvidenceSource;
  note: string;
  created_by: string;
  updated_by: string;
  updated_at: string;
  row_version: number;
};

/** D25: ממצא מביקור קודם באתר — עם מועד ומצב הביקור שבו נרשם. */
export type PreviousFindingRow = FindingRow & {
  visit_id: string;
  visit_date: string;
  visit_opened_at: string;
  visit_status: VisitRow['status'];
};

/** D28: ביקור באתר — למעקב (מועד, מצב, גרסת התוכן). */
export type SiteVisitRow = {
  id: string;
  visit_date: string;
  opened_at: string;
  status: VisitRow['status'];
  template_version_id?: string;
};

/** D28: תשובה מביקור קודם (סעיף שסומן ״לא תקין״ / ״דורש תיקון״, או הסעיף של ממצא). */
export type PreviousAnswerRow = {
  id: string;
  visit_id: string;
  question_id: string;
  state: AnswerValue | null;
  note: string;
};

/** D28: תחולת סעיף בביקור קודם (visit_item). */
export type PreviousItemRow = { visit_id: string; question_id: string; applicable: boolean };

/**
 * D32: סגירה — פריט שנכלל במסמך תיקונים מאושר של `closing_visit_id` (`followup_closure`): ממצא,
 * **או** ביקור מקורי + סעיף. סוגר את הפריט לביקורים מאוחרים מהביקור הסוגר בלבד.
 */
export type ClosureRow = {
  closing_visit_id: string;
  finding_id: string | null;
  source_visit_id: string | null;
  question_id: string | null;
};

/** הודעת שגיאה מהשרת לכללי שלב 4 — מה לתקן, בעברית. */
export function describeVisitError(message: string): string | null {
  if (/findings have no severity/.test(message))
    return 'יש ממצאים בלי רמת חומרה. בחרו מיידי / גבוה / נמוך לכל ממצא.';
  if (/defective items have no finding/.test(message))
    return 'יש סעיפים שסומנו כליקוי בלי ממצא. הוסיפו ממצא לכל אחד.';
  if (/not allowed in this chapter/.test(message)) return 'הסימון אינו מתאים לפרק זה.';
  if (/filtered out/.test(message)) return 'הסעיף סונן בפתיחת הביקור ואינו מקבל תשובה.';
  if (/not published/.test(message)) return 'אין גרסת צ׳קליסט שפורסמה. מנהל צריך לפרסם גרסה.';
  if (/applicability decision/.test(message))
    return 'חסרה הכרעת תחולה לחלק מהסעיפים — רעננו ונסו שוב.';
  if (/not active in the selected areas/.test(message))
    return 'אחד השלבים שנבחרו אינו פעיל ביום הביקור באזורים שנבחרו.';
  if (/published checklist content cannot change/.test(message))
    return 'גרסה שפורסמה אינה ניתנת לעריכה. צרו טיוטה חדשה כהעתק.';
  if (/empty checklist/.test(message)) return 'אי אפשר לפרסם גרסה ריקה.';
  if (/cannot be restored/.test(message)) return 'ממצא או תמונה שבוטלו אינם חוזרים.';
  if (/visit is not a draft/.test(message)) return 'הביקור כבר הוגש.';
  if (/do not belong to this visit/.test(message))
    return 'הסעיף אינו שייך לגרסת הצ׳קליסט של הביקור הזה. רעננו ונסו שוב.';
  if (/reason is required/.test(message)) return 'חסרה סיבה להסרה או להחזרה של הסעיף.';
  // שלב 6ב — מעקב ממצאים (D25).
  if (/follow-up is for earlier visits/.test(message))
    return 'מעקב נרשם רק לממצא מביקור קודם, לא לממצא של הביקור הזה.';
  if (/finding was withdrawn/.test(message)) return 'הממצא נמחק — אין לו מעקב.';
  if (/was not submitted/.test(message)) return 'הממצא מביקור שטרם הוגש — אין לו מעקב עדיין.';
  if (/finding is from a later visit/.test(message))
    return 'הממצא מביקור מאוחר יותר — אינו ממצא קודם לביקור הזה.';
  if (/no follow-up record/.test(message))
    return 'תמונת ״אחרי״ נרשמת רק אחרי שסומן מצב המעקב של הפריט. סמנו מצב ונסו שוב.';
  // D28 — סעיף בלי ממצא, דוח תיקונים.
  if (/item is from this visit/.test(message))
    return 'מעקב נרשם רק לסעיף מביקור קודם, לא לסעיף של הביקור הזה.';
  if (/item is from a later visit/.test(message))
    return 'הסעיף מביקור מאוחר יותר — אינו פריט קודם לביקור הזה.';
  if (/item was not marked defective/.test(message))
    return 'הסעיף לא סומן ״לא תקין״ בביקור הקודם — אין לו מעקב.';
  if (/item was removed from that visit/.test(message))
    return 'הסעיף הוסר מהביקור הקודם — אין לו מעקב.';
  if (/item has a finding/.test(message)) return 'לסעיף יש ממצא — רושמים את המעקב דרך הממצא.';
  if (/follow-ups are final/.test(message))
    return 'דוח התיקונים של הביקור כבר אושר — רשומות המעקב שלו נעולות.';
  if (/already approved for this visit/.test(message))
    return 'דוח התיקונים של הביקור כבר אושר — אין סקירה נוספת.';
  if (/no open items/.test(message))
    return 'אין פריטים פתוחים מביקורים קודמים — אין דוח תיקונים להפיק.';
  // שלב 6 — דוחות.
  if (/changed since review/.test(message))
    return 'משהו בביקור או באתר השתנה מאז הסקירה. סקרו שוב את הדוח לפני האישור.';
  if (/not a visit report|not a risk alert|not a corrections report/.test(message))
    return 'סוג הדוח אינו מתאים לפעולה. רעננו ונסו שוב.';
  if (/signer has no full name/.test(message))
    return 'לא הוגדר שם מלא בחשבון שלכם — הדוח נחתם בשם המחובר בלבד. הגדירו שם וחזרו.';
  if (/must be submitted before review/.test(message)) return 'יש להגיש את הביקור לפני הסקירה.';
  if (/does not match the reviewed report/.test(message))
    return 'האישור אינו תואם את מה שנסקר. רעננו וסקרו שוב.';
  if (/not awaiting approval|visit is not reviewed/.test(message))
    return 'הדוח כבר אושר או הוחלף בסקירה חדשה. רעננו.';
  if (/approved and cannot be reopened/.test(message))
    return 'הדוח אושר — הביקור אינו חוזר לטיוטה.';
  if (/only a ready report file/.test(message)) return 'אפשר למחוק רק קובץ דוח מוכן.';
  // 6ה-1 — מחיקה לצמיתות (D27).
  if (/only an organization admin can permanently delete/.test(message))
    return 'רק מנהל הארגון רשאי למחוק לצמיתות ביקור שהוגש, דוח מאושר, או ביקור שביקור אחר תיעד מעקב על פריטיו.';
  if (/only an organization admin can delete this draft/.test(message))
    return 'האתר בארכיון — רק מנהל הארגון רשאי למחוק את הטיוטה.';
  if (/visit not found/.test(message)) return 'הביקור כבר לא קיים — ייתכן שנמחק במכשיר אחר.';
  if (/only a pending render/.test(message)) return 'אפשר לבטל רק הפקה שעדיין ממתינה.';
  return null;
}
